import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import InfoRounded from '@mui/icons-material/InfoRounded';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import IconButton from '@mui/material/IconButton';
import { SelectChangeEvent } from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import useTheme from '@mui/material/styles/useTheme';
import Typography from '@mui/material/Typography';

import { getSitesInfoQueryKey, useAccountHostingList } from '@newfold/huapi-js';
import {
  HostingSitesV2200ItemsItem,
  SiteInfoResponseModelV2,
} from '@newfold/huapi-js/src/index.schemas';

import { useAlert } from '~/components/Alerts/alertsStore';
import CommonTooltip from '~/components/CommonTooltip';
import { invalidateMultipleQueries } from '~/components/MFEProvider/lib/HttpClient/queryClient';
import ResponsiveTable from '~/components/VisionUI/ResponsiveTable';
import SearchBar from '~/components/VisionUI/SearchBar';
import { useBrandInfo } from '~/hooks/useBrandInfo';
import useFilteredHostingSites from '~/hooks/useFilteredHostingSites';
import useFilteredSitesV3 from '~/hooks/useFilteredSitesV3';
import useHostingInfo from '~/hooks/useHostingInfo';
import useRefetchInterval from '~/hooks/useRefetchInterval';
import AddSiteButton from '~/scenes/Hosting/components/AddSiteButton';
import {
  SITE_LIST_PAGE_SIZE,
  SITE_LIST_PAGE_SIZES,
} from '~/scenes/Sites/utils/constants';
import { isWpStagingSite } from '~/utils/site/helpers';

import DBWarningModal from './components/DBWarningModal';
import SitesCards from './components/SitesCard';
import SitesEmpty from './components/SitesEmpty';
import SitesTableBody from './components/SitesTableBody';
import WarningModal from './components/WarningModal';
import {
  FAILED_STATUSES_WITH_SYNC_CHECK,
  PENDING_STATUSES,
} from './utils/constants';

type SitesPropOptions = {
  showAll?: boolean;
};
// TODO: add target path support `?i=` like in SiteCard.js
const Sites = ({
  showAll = false, // show all sites, minus any features that should not show when all are visible
}: SitesPropOptions) => {
  const { t } = useTranslation('sites');
  const theme = useTheme();
  const navigate = useNavigate();
  const generateAlert = useAlert();
  const queryClient = useQueryClient();
  const [modals, setModals] = useState({
    siteLimit: false,
    dbLimit: false,
  });

  const { brandFromJWT } = useBrandInfo();

  const isBluehost = brandFromJWT.toLowerCase() === 'bluehost';
  const [hasPendingSite, setHasPendingSite] = useState(false);

  const [pendingSiteQueries, setPendingSiteQueries] = useState<any[]>([]);

  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(SITE_LIST_PAGE_SIZE);

  const { refetchTime } = useRefetchInterval();

  const headCells = [
    {
      id: 'name',
      label: t('sitesTable.cells.0'),
      sort: true,
    },
    {
      id: 'type',
      label: t('sitesTable.cells.2'),
    },
    {
      id: 'login',
    },
  ];

  // API: GET all hosting plans
  const {
    data: hostingList,
    refetch: getHostingAccounts,
    isLoading: loadingHostingAccounts,
  } = useAccountHostingList();

  const { hostingId: accountId, isDisabled } = useHostingInfo({
    hostingList: hostingList?.data?.accounts,
  });

  const siteListParams = { limit: pageSize, page: page, search: '' };
  if (searchQuery) {
    siteListParams['search'] = searchQuery;
  }

  const {
    data,
    isPending,
    isSuccess: isSuccessHostingSites,
    // isError, // TODO: handle error, see comments below
  } = useFilteredHostingSites(accountId, siteListParams, {
    query: {
      enabled: !showAll,
      refetchInterval: hasPendingSite && refetchTime,
      refetchIntervalInBackground: hasPendingSite,
    },
  });

  // API: GET sites for all hosting plans
  const { data: v3Sites, isPending: v3SitesPending } = useFilteredSitesV3(
    siteListParams,
    {
      query: { enabled: showAll },
    },
  );

  // onSuccess - useFilteredHostingSites
  useEffect(() => {
    if (isSuccessHostingSites) {
      (async () => {
        const sites = data?.data?.items;
        const pendingSites = sites?.filter(({ status, jobs }: any) => {
          if (FAILED_STATUSES_WITH_SYNC_CHECK.includes(status)) {
            return jobs && Object.values(jobs).includes('sync');
          }
          return PENDING_STATUSES.includes(status);
        }) as HostingSitesV2200ItemsItem[];

        const newPendingSiteQueryKeys =
          (pendingSites &&
            pendingSites
              .map((site) => getSitesInfoQueryKey(Number(site?.id)))
              .filter(
                (newKey) =>
                  !pendingSiteQueries.some((existingKey) => {
                    // Deep equality check for query keys
                    if (Array.isArray(newKey) && Array.isArray(existingKey)) {
                      return (
                        newKey.length === existingKey.length &&
                        newKey.every(
                          (item, index) => item === existingKey[index],
                        )
                      );
                    }
                    return newKey === existingKey;
                  }),
              )) ||
          [];

        const hasNewPendingSiteQueries = newPendingSiteQueryKeys.length > 0;

        const hasPendingSites = pendingSites?.length > 0;
        const hasPendingSiteQueries = pendingSiteQueries?.length > 0;
        const donePolling = !hasPendingSites && hasPendingSiteQueries;

        // Track sites that are pending, once they become active,
        // invalidate the site info call(s) to keep cache in sync

        if (hasNewPendingSiteQueries) {
          // Only add new query keys to the existing list
          setPendingSiteQueries((prevKeys) => [
            ...new Set([...prevKeys, ...newPendingSiteQueryKeys]),
          ]);
        }
        if (donePolling) {
          await invalidateMultipleQueries(queryClient, pendingSiteQueries);
          setPendingSiteQueries([]);
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isSuccessHostingSites]);

  const sites = !showAll
    ? (data?.data?.items as HostingSitesV2200ItemsItem[])
    : (v3Sites?.data?.items as SiteInfoResponseModelV2[]);

  const maxSites = data?.data?.max_sites;
  const maxAllowedSitesFormatted = maxSites === null ? 'Unlimited' : maxSites;
  const usedSites = data?.data?.used_sites;

  //active, staging and misconfigured sites count
  const misConfiguredSitesCount =
    data?.data?.used_sites_details?.sites_misconfigured;
  const activeSitesCount = data?.data?.used_sites_details?.sites_active;
  const stagingSitesCount = data?.data?.used_sites_details?.staging_sites;

  useEffect(() => {
    setHasPendingSite(
      sites?.some(({ status, jobs }: any) => {
        if (FAILED_STATUSES_WITH_SYNC_CHECK.includes(status)) {
          return jobs && Object.values(jobs).includes('sync');
        }
        return PENDING_STATUSES.includes(status);
      }),
    );
  }, [sites]);

  const showNoSitesAvailableModal = () =>
    setModals((prev) => ({ ...prev, siteLimit: true }));

  const closeSiteLimitModal = () =>
    setModals((prev) => ({ ...prev, siteLimit: false }));

  const closeDBLimitModal = () =>
    setModals((prev) => ({ ...prev, dbLimit: false }));

  const handleSearch = (value: string) => {
    setPage(1);
    setSearchQuery(value);
  };

  const handleMultiHostingAddSite = async () => {
    const { data, isError } = await getHostingAccounts();
    const accounts = data?.data?.accounts;

    if (isError || !accounts) {
      return generateAlert({
        severity: 'error',
        description: t('addSiteErrMsg'),
        showCloseBtn: true,
        persist: false,
      });
    }

    if (accounts?.length === 1) {
      const { current_sites, max_sites } = accounts[0];

      const maxSitesValue =
        max_sites === 'unlimited' ? Infinity : parseInt(max_sites!) || null;

      const availableSites = maxSitesValue
        ? Math.max(maxSitesValue - current_sites!, 0)
        : null;

      if (!availableSites) {
        return showNoSitesAvailableModal();
      }

      return navigate('./add');
    }

    if (accounts?.length > 1) {
      window.location.href = '/my-account/hosting/list';
      return;
    }
  };

  const siteListWithHosting = sites
    ?.map((siteInstance) => ({
      ...siteInstance,
      hosting: hostingList?.data?.accounts?.find(
        (account) =>
          Number(account.hosting_id) === Number(siteInstance?.account_id),
      ),
    }))
    .filter((site) => site.hosting);

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number,
  ) => {
    setPage(value);
  };

  const handlePageSizeChange = (event: SelectChangeEvent<typeof pageSize>) => {
    const value = event.target.value;
    setPageSize(value as unknown as number);
    setPage(1);
  };
  const handleLicenseNavigate = () => {
    window.location.assign('/my-account/product-licenses');
  };
  const pageCount = showAll ? v3Sites?.data?.pages : data?.data?.pages;
  const pageLimit = showAll ? v3Sites?.data?.limit : data?.data?.limit;
  const noSites = siteListWithHosting?.length < 1;
  const emptyListCopy = noSites ? t('noSites') : t('noSitesAfterSearch');
  const isPendingAll =
    loadingHostingAccounts || (showAll ? v3SitesPending : isPending);
  const numOfNonStagingSites = siteListWithHosting?.filter(
    (site) => !isWpStagingSite(site.docpath ?? undefined),
  );
  const numOfSites = numOfNonStagingSites?.length || 0;

  return (
    <>
      <Card>
        <CardHeader
          disableTypography
          sx={{
            flexDirection: { xs: 'column', md: 'row' }, // Column layout for mobile, row for desktop
            alignItems: { xs: 'flex-start', md: 'center' }, // Align items properly in column layout
            gap: { xs: 2, md: 0 }, // Add spacing between title and action in mobile view
          }}
          action={
            <Stack direction={{ sx: 'column', md: 'row' }} gap={2}>
              {isBluehost && (
                <Button variant="outlined" onClick={handleLicenseNavigate}>
                  {t('manageSubscriptions')}
                </Button>
              )}
              {!(showAll && numOfSites === 0) && (
                <AddSiteButton
                  showingAllSites={showAll}
                  isPending={isPendingAll}
                />
              )}
            </Stack>
          }
          title={
            <Typography variant="h2" component="span" sx={{ mr: 1 }}>
              {t('sitesListTitle')}
            </Typography>
          }
          subheader={
            !showAll
              ? maxAllowedSitesFormatted && (
                  <Typography
                    variant="h4"
                    component="span"
                    sx={{ color: theme.palette.grey[500] }}
                  >
                    {
                      <>
                        <b>{usedSites || 0} Used</b> of{' '}
                        <b>{maxAllowedSitesFormatted}</b>
                        {(misConfiguredSitesCount ?? 0) > 0 && (
                          <CommonTooltip
                            placement="top"
                            title={
                              <>
                                {t('sitesActiveCount', {
                                  activeSitesCount: activeSitesCount ?? 0,
                                })}
                                <br />
                                {t('sitesMisconfiguredCount', {
                                  misConfiguredSitesCount:
                                    misConfiguredSitesCount ?? 0,
                                })}
                                <br />
                                {t('stagingSitesCount', {
                                  stagingSitesCount: stagingSitesCount ?? 0,
                                })}
                              </>
                            }
                          >
                            <IconButton>
                              <InfoRounded fontSize="small" />
                            </IconButton>
                          </CommonTooltip>
                        )}
                      </>
                    }
                  </Typography>
                )
              : numOfSites >= 0 && (
                  <Typography
                    variant="h4"
                    component="span"
                    sx={{ color: theme.palette.grey[500] }}
                  >
                    <Trans
                      i18nKey={
                        numOfSites === 1
                          ? 'sites:numOfSites_one'
                          : 'sites:numOfSites_other'
                      }
                      values={{ numOfSites }}
                    />
                  </Typography>
                )
          }
        />
        <CardContent>
          <Stack spacing={3}>
            {/* Search & Toggle Bar */}

            <SearchBar
              searchQuery={searchQuery}
              placeholder={t('searchSites')}
              handleSearch={handleSearch}
              inputProps={{ sx: { width: { xs: '100%', sm: 400 } } }}
            />

            {/* Error State */}
            {/* TODO: Visit your hosting plan to purchase a site <Link href=".."> */}
            {/* {isError && <Typography></Typography>} */}

            <ResponsiveTable
              type="sites"
              showCardsUI={false}
              headCells={headCells}
              list={siteListWithHosting}
              isPending={isPendingAll}
              mobile={{ ui: <SitesCards /> }}
              // @ts-expect-error
              emptyListCopy={emptyListCopy}
              paginationProps={{
                page: page,
                pageCount: pageCount,
                pageLimit: pageLimit,
                pageSize: pageSize,
                pageSizes: SITE_LIST_PAGE_SIZES,
                onPageChange: handlePageChange,
                onPageSizeChange: handlePageSizeChange,
                totalRows: showAll ? v3Sites?.data?.total : data?.data?.total,
              }}
              {...(sites && {
                empty: (
                  <SitesEmpty
                    showAll={showAll}
                    noSites={noSites}
                    emptyListCopy={emptyListCopy}
                    handleAddSite={handleMultiHostingAddSite}
                  />
                ),
              })}
              {...(!showAll && {
                showAllCTA: (
                  <Button
                    disabled={isDisabled || isPendingAll}
                    disableRipple
                    href="/sites"
                  >
                    {t('viewAll')}
                  </Button>
                ),
              })}
            >
              <SitesTableBody />
            </ResponsiveTable>
          </Stack>
        </CardContent>
      </Card>
      <WarningModal open={modals.siteLimit} onClose={closeSiteLimitModal} />
      <DBWarningModal open={modals.dbLimit} onClose={closeDBLimitModal} />
    </>
  );
};

export default Sites;
